/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useContext } from 'react';
import { DataTable } from '@scuf/datatable';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import descriptors from '../../../../locales/en-US/descriptors';
import { useTranslation } from '@scuf/localization';
import CommonProgressBar from '../../../shared/CommonProgressBar/CommonProgressBar';
import './EmaasEnergyUsagesBreakdownByMeterTheme.scss';
import GasIcon from '../../../../images/GasIcon.svg';
import ElectricityIcon from '../../../../images/Energy-icon.svg';
import {
  EnergyUsagesDetailExpandMeterField,
  EnergyUsageScopeType,
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  MAGIC_VALUE
} from '../../../shared/Utils/Constants';
import {
  getConvertedUnit,
  getConvertedValues,
  getNullConverted
} from '../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import { getBadge } from '../../../shared/Utils/Graph/AllGraphUtils';
import {
  getPercentageColor,
  rowExpansionTemplate
} from '../../PortfolioEnergyUsageViewDetails/PremiumSubscription/AllUtilitiesDetails/BottomSection/TableColumnRenderUtils';
import TruncateText from '../../../shared/TruncatedText/TruncateText';
import { IAny } from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { IEnergyBreakdownByMeter } from './IEnergyUsagesBreakdownProps';
import {
  expanderTemplate,
  renderTableColumns
} from '../../../shared/MeterSavingsDetailTable/MeterSavingsDetailTableUtility';
import { ApiContext } from '../../../../context/ApiContext';
import { ConversionType, getConvertedValue } from '../../../../main';

const getMeterHeader = (isNILM: boolean | undefined) => {
  return isNILM
    ? EnergyUsagesDetailExpandMeterField.meter
    : EnergyUsagesDetailExpandMeterField.utilityType;
};

const nilmCssCheckEnergyUsageBreakdownConsmp = (
  props: IEnergyBreakdownByMeter
) =>
  props.isNILM
    ? 'measurementunit NILM-EnergyUsage-Breakdown-Consmp'
    : 'measurementunit';

const nilmCssCheckEnergyUsageBreakdown = (props: IEnergyBreakdownByMeter) =>
  props.isNILM
    ? 'measurementunit NILM-EnergyUsage-Breakdown'
    : 'measurementunit';

const nilmCssDataTableCheck = (props: IEnergyBreakdownByMeter) =>
  props.isNILM ? '1%' : '2%';

const emaasDetailBreakdownByMeter = (props: IEnergyBreakdownByMeter) => {
  const { meterData, columnDetails, energyMeasurementUnit } = props;
  const [expandedRows, setexpandedRows] = useState([meterData[0]]);
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const [fieldSortOrder, setFieldSortOrder] = useState<number>(-1);
  const [sortFieldName, setSortFieldName] = useState<string>(
    EnergyUsagesDetailExpandMeterField.actualConsumption
  );

  const getLabel = (electIc: boolean, gasIc: boolean) => {
    return electIc ? t(d.Electricity) : gasIc ? t(d.Gas) : undefined;
  };
  const cellDataRenderer = (cellData: ICellData) => {
    const tableFirstRow = getMeterHeader(props.isNILM);
    const { value, field, rowData } = cellData;

    switch (field) {
      case EnergyUsagesDetailExpandMeterField.actualConsumption:
        const { totalActualEmission: convertedValue } = rowData;
        return (
          <span className="score-category-meterdata">
            <span className="score-category-percent">
              <div className="measurementunit">
                {
                  getConvertedValue(
                    ConversionType?.ELECTRICITY,
                    energyMeasurementUnit ? energyMeasurementUnit : '', //utilityUnit,
                    convertedValue,
                    rowData.actualUnit
                  ).Value
                }
                {/* {getNullConverted(convertedValue)} */}
              </div>
              {getConvertedUnit(convertedValue, rowData.actualUnit)}
            </span>
            {
              <span className="score-category-progress">
                <CommonProgressBar
                  actual={rowData.rawTotalActualEmission}
                  previousPeriod={rowData.rawTotalPreviousPeriodEmission}
                  savings={rowData.rawTotalDeviation}
                  savingsPercentage={rowData.percentageEmissionsSaving}
                />
              </span>
            }
          </span>
        );
        break;
      case tableFirstRow:
        return (
          <span className="score-category-meterdata">
            <span className="score-category-percent">
              <div className="utilityType">{rowData.utilityType}</div>
            </span>
          </span>
        );
        break;
      case EnergyUsagesDetailExpandMeterField.change:
        const { totalDeviation: convertedValu, deviationScaledData } = rowData;
        return convertedValu !== null ? (
          <span className="score-category-meterdata-meter">
            <span
              className="savings-badge"
              style={{
                color: getPercentageColor(
                  props.isNILM
                    ? rowData.totalDeviationPercentage
                    : rowData.percentageEmissionsSaving
                )
              }}
            >
              {
                getConvertedValue(
                  ConversionType?.ELECTRICITY,
                  energyMeasurementUnit ? energyMeasurementUnit : '', //utilityUnit,
                  convertedValu === null ? null : Math.abs(convertedValu),
                  rowData.changeUnit
                ).Value
              }
              {/* {getConvertedValues(convertedValu, deviationScaledData)} */}
              {' ' +
                getConvertedUnit(
                  convertedValu,
                  rowData.changeUnit,
                  deviationScaledData
                ) +
                ' ' +
                '(' +
                getConvertedValues(
                  props.isNILM
                    ? rowData.totalDeviationPercentage
                    : rowData.percentageEmissionsSaving
                ) +
                '%' +
                ')'}
            </span>
          </span>
        ) : (
          <span className="score-category-meterdata-meter">--</span>
        );
        break;
      case EnergyUsagesDetailExpandMeterField.meter:
        const electIc =
          rowData.utilityType.toLowerCase() === EnergyUsageScopeType.ELECTRIC;
        const gasIc =
          rowData.utilityType.toLowerCase() === EnergyUsageScopeType.Gas;
        return (
          <>
            <span className="score-category-meterdata-meter ">
              {getBadge(rowData.totalDeviation)}
              <img
                className="card-electricity-icon"
                src={electIc ? ElectricityIcon : GasIcon}
              />
              <span className="score-category-meterparent">
                {' '}
                {getLabel(electIc, gasIc)}
              </span>
            </span>
          </>
        );
        break;
      case EnergyUsagesDetailExpandMeterField.baselineConsumption:
        const { totalPreviousPeriodEmission: convertedVal } = rowData;
        return value === null ? (
          <>--</>
        ) : (
          <span className="score-category-meterdata-meter">
            <div className="consumptionvalue">
              {
                getConvertedValue(
                  ConversionType?.ELECTRICITY,
                  energyMeasurementUnit ? energyMeasurementUnit : '', //utilityUnit,
                  convertedVal,
                  rowData.baselineUnit
                ).Value
              }
              {/* {getNullConverted(convertedVal)} */}
            </div>
            {getConvertedUnit(convertedVal, rowData.baselineUnit)}
          </span>
        );
        break;
      default:
        return <></>;
        break;
    }
  };

  const rowcellDataRenderer = (cellData: ICellData) => {
    const { value, field, rowData } = cellData;
    switch (field) {
      case EnergyUsagesDetailExpandMeterField.actualConsumption:
        const { totalActualEmission: convertedValue } = rowData;
        return (
          <span className="emaas-carbondetail-category-meterdata">
            <span className="score-category-percent">
              <div className={nilmCssCheckEnergyUsageBreakdownConsmp(props)}>
                {
                  getConvertedValue(
                    ConversionType?.ELECTRICITY,
                    energyMeasurementUnit ? energyMeasurementUnit : '', //utilityUnit,
                    convertedValue,
                    rowData.actualUnit
                  ).Value
                }
                {/* {getNullConverted(convertedValue)} */}
              </div>
              {getConvertedUnit(convertedValue, rowData.actualUnit)}
            </span>
            {
              <span className="score-category-progress">
                <CommonProgressBar
                  actual={rowData.totalActualEmission}
                  previousPeriod={rowData.totalPreviousPeriodEmission}
                  savings={rowData.totalDeviation}
                  savingsPercentage={rowData.totalDeviationPercentage}
                  progessBarRelativeWidth={rowData.progessBarRelativeWidth}
                />
              </span>
            }
          </span>
        );
        break;
      case EnergyUsagesDetailExpandMeterField.meter:
        if (
          rowData.utilityType.toLowerCase() === EnergyUsageScopeType.ELECTRIC ||
          rowData.utilityType === EnergyUsageScopeType.ELECTRICITY
        ) {
          return (
            <span className="score-category-meterdata-meter ">
              <span className="score-category-Badge">
                <span className="score-category-percent">
                  <div className={nilmCssCheckEnergyUsageBreakdown(props)}>
                    <TruncateText
                      text={rowData.meterName}
                      maxLength={MAGIC_VALUE.THIRTY}
                    />
                  </div>
                </span>
              </span>
            </span>
          );
        }
        if (rowData.utilityType.toLowerCase() === EnergyUsageScopeType.Gas) {
          return (
            <span className="score-category-meterdata-meter ">
              <span className="score-category-Badge">
                <span className="score-category-percent">
                  <div className="measurementunit">
                    <TruncateText
                      text={rowData.meterName}
                      maxLength={MAGIC_VALUE.THIRTY}
                    />
                  </div>
                </span>
              </span>
            </span>
          );
        }
        break;
      case EnergyUsagesDetailExpandMeterField.baselineConsumption:
        const { totalPreviousPeriodEmission: convertedVal } = rowData;
        return (
          <span className="score-category-meterdata-meter">
            {
              getConvertedValue(
                ConversionType?.ELECTRICITY,
                energyMeasurementUnit ? energyMeasurementUnit : '', //utilityUnit,
                convertedVal,
                rowData.baselineUnit
              ).Value
            }
            {/* {getNullConverted(convertedVal)} */}{' '}
            {getConvertedUnit(convertedVal, rowData.baselineUnit)}
          </span>
        );
        break;
      case EnergyUsagesDetailExpandMeterField.change:
        const { totalDeviation: convertedValu, deviationScaledData } = rowData;

        return convertedValu !== null ? (
          <span className="score-category-meterdata-meter">
            <span
              className="savings-badge"
              style={{
                color: getPercentageColor(rowData.totalDeviationPercentage)
              }}
            >
              {
                getConvertedValue(
                  ConversionType?.ELECTRICITY,
                  energyMeasurementUnit ? energyMeasurementUnit : '', //utilityUnit,
                  convertedValu === null ? null : Math.abs(convertedValu),
                  rowData.changeUnit
                ).Value
              }
              {/* {getConvertedValues(convertedValu, deviationScaledData)} */}
              {' ' +
                getConvertedUnit(
                  convertedValu,
                  rowData.changeUnit,
                  deviationScaledData
                ) +
                ' ' +
                '(' +
                getConvertedValues(
                  rowData.totalDeviationPercentage?.toFixed(
                    KPI_PERCENTAGE_DECIMAL_PRECISION
                  )
                ) +
                '%' +
                ')'}
            </span>
          </span>
        ) : (
          <span className="score-category-meterdata-meter">--</span>
        );
        break;
      case EnergyUsagesDetailExpandMeterField.utilityType:
        return (
          <span className="score-category-meterdata">
            <span className="score-category-percent">
              <div className="utilityType">{rowData.utilityType}</div>
            </span>
          </span>
        );
        break;
      default:
        return <></>;
        break;
    }
    return <></>;
  };
  return (
    <div className="Savings-Datatable">
      <DataTable
        className="nonflat-table Savingtable"
        expandedRows={expandedRows}
        scrollable={true}
        scrollHeight="20rem"
        rowExpansionTemplate={(data: IAny) =>
          rowExpansionTemplate(
            data,
            columnDetails,
            rowcellDataRenderer,
            props.isNILM
          )
        }
        data={meterData}
        onSort={({ sortField, sortOrder }) => {
          setFieldSortOrder(sortOrder as number);
          setSortFieldName(sortField);
        }}
        sortField={sortFieldName}
        sortOrder={fieldSortOrder}
      >
        <DataTable.Column
          initialWidth={nilmCssDataTableCheck(props)}
          field=""
          header=""
          renderer={(data: ICellData) =>
            expanderTemplate(data, expandedRows, setexpandedRows)
          }
        />
        {renderTableColumns(columnDetails, cellDataRenderer)}
      </DataTable>
    </div>
  );
};
export default emaasDetailBreakdownByMeter;
